/**
 * scheduleAllocationBands.ts
 * Staffing Allocation % — the color-band mapping for the Employee Weekly
 * Capacity grid (PR B / D5 of qa/staffing-allocation-2026-07-13/PLAN.md).
 *
 * Under the reinstated part-time allocation model a person's weekly utilization
 * is Σ (their share of each work-order / quote that week) — see
 * weeklyCapacityService.allocationPercent (presence-fallback 100). This module
 * turns that percent into one of five bands so the capacity grid can show, per
 * person per week, "Adin 125% (over) / Ibrahim 50% (light) / Shawn 90% (at
 * capacity)" at a glance.
 *
 * Thresholds (D5 — admin-tunable later; kept here as the single source):
 *   - none    →  no allocation at all (0%) — an absence-only week, render blank.
 *   - light   →  1–49%  — clearly under-utilized (has room).
 *   - healthy →  50–85% — comfortably loaded.
 *   - at      →  86–100% — at capacity.
 *   - over    →  >100%  — over-allocated (D4 warn-only; never blocks).
 *
 * PURE — no React / MUI / theme. Colors live in the components (theme.schedule.*
 * tokens) per CODING-STANDARDS; this module only decides the BAND + the numbers.
 */

/** The five utilization bands (D5). */
export type AllocationBand = 'none' | 'light' | 'healthy' | 'at' | 'over';

/** Band lower bounds (inclusive) — the single tunable source. */
export const ALLOCATION_BAND_THRESHOLDS = {
  /** ≥ this and <healthy → light (under-utilized). */
  light: 1,
  /** ≥ this and <at → healthy. */
  healthy: 50,
  /** ≥ this and ≤100 → at capacity. */
  at: 86,
  /** > 100 → over. */
  over: 101,
} as const;

/**
 * Map a weekly allocation percent to its band.
 *
 * @param percent Σ allocation % for the (person, week). 0 / negative / NaN → 'none'.
 */
export function allocationBand(percent: number | null | undefined): AllocationBand {
  const p = typeof percent === 'number' && Number.isFinite(percent) ? percent : 0;
  if (p <= 0) return 'none';
  if (p < ALLOCATION_BAND_THRESHOLDS.healthy) return 'light';
  if (p < ALLOCATION_BAND_THRESHOLDS.at) return 'healthy';
  if (p <= 100) return 'at';
  return 'over';
}

/** Short human label for a band (used in tooltips / legend). */
export function allocationBandLabel(band: AllocationBand): string {
  switch (band) {
    case 'none':    return 'no allocation';
    case 'light':   return 'under capacity';
    case 'healthy': return 'healthy';
    case 'at':      return 'at capacity';
    case 'over':    return 'over capacity';
  }
}

/**
 * The percent as a display string ("125%"), or an em dash when there is no
 * allocation this week (band 'none').
 */
export function allocationPercentLabel(percent: number | null | undefined): string {
  const band = allocationBand(percent);
  if (band === 'none') return '—';
  return `${Math.round(percent as number)}%`;
}
